/**
 * Main-thread proxy for the Dedicated Worker PatternPlayer (music path).
 * Forwards KernelMessages into AudioTransport and mirrors play/mute state.
 *
 * Late worker→main note traffic after stop is dropped via `playing` +
 * `scheduleGen` (lookahead races, especially open-hold C--).
 */
import { MAX_INSTRUMENT_SLOT_COUNT, type InstrumentSlot, type KernelMessage } from "../types";
import type { ClipIR } from "../clip/types";
import type { PlayheadInfo } from "../clip/patternPlayer";
import type { AudioEngine } from "./engine";
import type { SchedulerMainToWorker, SchedulerWorkerToMain } from "./schedulerProtocol";
import { acceptWorkerKernel } from "./schedulerProtocol";

const CLOCK_SYNC_MS = 100;

export type SchedulerProxyOptions = {
  workerUrl: string;
  engine: AudioEngine;
  lookahead?: number;
  intervalMs?: number;
  onPlayhead?: (info: PlayheadInfo | undefined) => void;
  onEnded?: () => void;
  onError?: (message: string) => void;
  /**
   * Optional observer for timed note traffic (e.g. External MIDI sink).
   * Called after the play/stop gate accepts the message, before transport.
   */
  onKernelMessage?: (message: KernelMessage) => void;
};

/**
 * Drop-in music scheduler API matching the PatternPlayer methods AudioSession uses.
 */
export class SchedulerProxy {
  private worker: Worker | undefined;
  private ready = false;
  private readonly pending: SchedulerMainToWorker[] = [];
  private playing = false;
  /** Bumped on play/stop so in-flight worker kernel msgs from an older run are ignored. */
  private scheduleGen = 0;
  private readonly muted: boolean[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => false);
  private readonly solo: boolean[] = Array.from({ length: MAX_INSTRUMENT_SLOT_COUNT }, () => false);
  private clockTimer: ReturnType<typeof setInterval> | undefined;
  private failed = false;
  /** Coalesce playhead UI to rAF so kernel note forwarding isn't blocked. */
  private playheadRaf: number | undefined;
  private pendingPlayhead: PlayheadInfo | undefined;
  private hasPendingPlayhead = false;

  constructor(private readonly options: SchedulerProxyOptions) {
    try {
      this.worker = new Worker(options.workerUrl, { type: "classic" });
      this.worker.onmessage = (event: MessageEvent<SchedulerWorkerToMain>) => {
        this.onWorkerMessage(event.data);
      };
      this.worker.onerror = event => {
        this.failed = true;
        this.options.onError?.(event.message || "Scheduler worker error");
      };
    } catch (error) {
      this.failed = true;
      this.options.onError?.(
        error instanceof Error ? error.message : String(error),
      );
    }
    this.send({
      type: "init",
      lookahead: options.lookahead ?? 0.5,
      intervalMs: options.intervalMs ?? 25,
    });
  }

  /** True when the worker failed to start (caller should fall back to main). */
  hasFailed(): boolean {
    return this.failed;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  getMuted(): boolean[] {
    return [...this.muted];
  }

  getSolo(): boolean[] {
    return [...this.solo];
  }

  setClip(clip: ClipIR): void {
    // Same race as stop: worker may still deliver already-posted C-- noteOns
    // for tracks that this clip update muted/emptied (Performance lane stop).
    // Bump generation + drop kernel lookahead before the worker resyncs.
    if (this.playing) {
      this.scheduleGen += 1;
      this.options.engine.transport.cancelPending(this.options.engine.getCurrentTime());
    }
    this.send({
      type: "setClip",
      clip: structuredClone(clip),
      scheduleGen: this.scheduleGen,
    });
  }

  start(clip: ClipIR, options?: { startStep?: number }): void {
    this.scheduleGen += 1;
    this.playing = true;
    this.startClockSync();
    this.send({
      type: "play",
      clip: structuredClone(clip),
      startStep: options?.startStep,
      audioNow: this.options.engine.getCurrentTime(),
      scheduleGen: this.scheduleGen,
    });
  }

  stop(): void {
    // Mark idle before the worker round-trip so lookahead noteOns already
    // queued on this port (or still being posted) cannot re-arm voices.
    this.playing = false;
    this.scheduleGen += 1;
    this.stopClockSync();
    this.send({ type: "stop", scheduleGen: this.scheduleGen });
  }

  setTrackMute(slot: InstrumentSlot, muted: boolean): void {
    this.muted[slot] = muted;
    this.send({ type: "setMuteSolo", muted: this.getMuted(), solo: this.getSolo() });
  }

  setTrackSolo(slot: InstrumentSlot, solo: boolean): void {
    this.solo[slot] = solo;
    this.send({ type: "setMuteSolo", muted: this.getMuted(), solo: this.getSolo() });
  }

  setMuteSolo(state: { muted?: boolean[]; solo?: boolean[] }): void {
    if (state.muted) {
      for (let i = 0; i < MAX_INSTRUMENT_SLOT_COUNT; i++) {
        this.muted[i] = Boolean(state.muted[i]);
      }
    }
    if (state.solo) {
      for (let i = 0; i < MAX_INSTRUMENT_SLOT_COUNT; i++) {
        this.solo[i] = Boolean(state.solo[i]);
      }
    }
    this.send({ type: "setMuteSolo", muted: this.getMuted(), solo: this.getSolo() });
  }

  setOpenHoldDefaultSlots(flags: boolean[]): void {
    this.send({ type: "setOpenHoldDefaultSlots", flags: flags.slice(0, MAX_INSTRUMENT_SLOT_COUNT) });
  }

  dispose(): void {
    this.stopClockSync();
    this.playing = false;
    this.scheduleGen += 1;
    if (this.playheadRaf !== undefined) {
      cancelAnimationFrame(this.playheadRaf);
      this.playheadRaf = undefined;
      this.hasPendingPlayhead = false;
      this.pendingPlayhead = undefined;
    }
    if (this.worker) {
      try {
        this.worker.postMessage({ type: "dispose" } satisfies SchedulerMainToWorker);
      } catch {
        // ignore
      }
      this.worker.terminate();
      this.worker = undefined;
    }
    this.ready = false;
    this.pending.length = 0;
  }

  private send(msg: SchedulerMainToWorker): void {
    if (!this.worker || this.failed) {
      return;
    }
    if (!this.ready && msg.type !== "init") {
      this.pending.push(msg);
      return;
    }
    this.worker.postMessage(this.withFreshClock(msg));
  }

  /** Refresh audioNow on play/clockSync so queued messages aren't stale. */
  private withFreshClock(msg: SchedulerMainToWorker): SchedulerMainToWorker {
    if (msg.type === "play") {
      return {
        ...msg,
        audioNow: this.options.engine.getCurrentTime(),
      };
    }
    if (msg.type === "clockSync") {
      return {
        type: "clockSync",
        audioNow: this.options.engine.getCurrentTime(),
      };
    }
    return msg;
  }

  private onWorkerMessage(msg: SchedulerWorkerToMain): void {
    switch (msg.type) {
      case "ready": {
        this.ready = true;
        for (const pending of this.pending) {
          this.worker?.postMessage(this.withFreshClock(pending));
        }
        this.pending.length = 0;
        break;
      }
      case "kernel": {
        this.applyKernelMessage(msg.message, msg.scheduleGen);
        break;
      }
      case "stopped": {
        // Anything that slipped between session allNotesOff and the drop gate.
        this.options.engine.transport.allNotesOff();
        break;
      }
      case "playhead": {
        // Defer UI: follow-scroll / React work must not stall kernel messages
        // still queued on this port (loop-boundary noteOns especially).
        this.pendingPlayhead = msg.info;
        this.hasPendingPlayhead = true;
        if (this.playheadRaf !== undefined) {
          break;
        }
        this.playheadRaf = requestAnimationFrame(() => {
          this.playheadRaf = undefined;
          if (!this.hasPendingPlayhead) {
            return;
          }
          this.hasPendingPlayhead = false;
          this.options.onPlayhead?.(this.pendingPlayhead);
        });
        break;
      }
      case "ended": {
        this.playing = false;
        this.scheduleGen += 1;
        this.stopClockSync();
        this.options.onEnded?.();
        break;
      }
      case "error": {
        // Non-fatal schedule errors — do not tear down the worker.
        console.warn("[scheduler-worker]", msg.message);
        break;
      }
      default:
        break;
    }
  }

  private applyKernelMessage(message: KernelMessage, scheduleGen: number): void {
    if (!acceptWorkerKernel(this.playing, this.scheduleGen, scheduleGen, message.type)) {
      return;
    }

    this.options.onKernelMessage?.(message);

    const transport = this.options.engine.transport;
    switch (message.type) {
      case "noteOn":
        transport.noteOn(
          message.slot,
          message.note,
          message.velocity,
          message.time,
          message.noteFx,
        );
        break;
      case "noteOff":
        transport.noteOff(message.slot, message.note, message.time);
        break;
      case "setNoteFx":
        transport.setNoteFx(
          message.slot,
          message.noteFx,
          message.time,
          message.velocity,
        );
        break;
      case "setBpm":
        transport.setBpm(message.bpm);
        break;
      case "allNotesOff":
        transport.allNotesOff();
        break;
      case "cancelPending":
        transport.cancelPending(message.afterTime);
        break;
      default:
        // Scheduler only emits note/bpm traffic.
        break;
    }
  }

  private startClockSync(): void {
    this.stopClockSync();
    const tick = () => {
      if (!this.playing || !this.worker || !this.ready) {
        return;
      }
      this.worker.postMessage({
        type: "clockSync",
        audioNow: this.options.engine.getCurrentTime(),
      } satisfies SchedulerMainToWorker);
    };
    tick();
    this.clockTimer = setInterval(tick, CLOCK_SYNC_MS);
  }

  private stopClockSync(): void {
    if (this.clockTimer !== undefined) {
      clearInterval(this.clockTimer);
      this.clockTimer = undefined;
    }
  }
}
